import { Button } from '@components/ui/button';
import { cn } from '@utils/index';
import { Eye, Sparkles, X } from 'lucide-react';
import React from 'react';

import type { FieldEvidence } from '@/components/fields/types';
import type { FormField } from '@/types/formSchema';

import { HintBanner } from './HintBanner';
import { SuggestionChip } from './SuggestionChip';

interface InputWithSuggestionProps {
  /**
   * The input component to wrap (Input, Select, DatePicker, etc.)
   */
  children: React.ReactNode;

  /**
   * The raw suggestion value from extracted_data
   */
  suggestion?: any;

  /**
   * Optional hint/warning text to display
   */
  hint?: string;

  /**
   * The field schema for formatting
   */
  field: FormField;

  /**
   * Current field value (to compare with suggestion)
   */
  value?: any;

  /**
   * Callback when user accepts the suggestion
   */
  onAccept: () => void;

  /**
   * Callback when user rejects the suggestion (optional - wrapper handles hiding)
   */
  onReject?: () => void;

  /**
   * Multi-value options ("PMS returned N values — choose one"). Rendered as a
   * chooser connected below the input; clicking an option commits its value
   * via onAcceptOption. Mutually exclusive with `suggestion` in practice.
   */
  options?: { value: string; label?: string }[];

  /**
   * Callback when user picks one of `options`.
   */
  onAcceptOption?: (value: string) => void;

  /**
   * Callback when user expands a hidden suggestion
   */
  onExpand?: () => void;

  /**
   * Whether the input is a textarea (needs stronger negative margin)
   */
  isTextarea?: boolean;

  /**
   * Whether the suggestion is collapsed (for textarea only, from localStorage)
   */
  isCollapsed?: boolean;

  /**
   * Optional className for the wrapper container
   */
  className?: string;

  /**
   * Validation error message (used for error styling)
   */
  error?: string;

  /**
   * Optional per-field extraction evidence. When present (with onNavigateToCall),
   * the suggestion chip renders the Review button + reasoning lightbulb.
   */
  evidence?: FieldEvidence[];
  sourceCallId?: string;
  onNavigateToCall?: (callId: string, evidenceTimeStart?: string, evidenceTimeEnd?: string) => void;
  onNavigateToAudit?: (auditId: string) => void;
}

/**
 * Clones a React element and removes its bottom border-radius by adding 'rounded-b-none'.
 * Also applies error styling if error is present.
 * Handles special cases like phone input (inline styles) and nested components (DatePicker).
 */
const removeBottomRadius = (child: React.ReactNode, error?: string): React.ReactNode => {
  if (!React.isValidElement(child)) {
    return child;
  }

  const childType = (child.type as any)?.displayName || (child.type as any)?.name || '';

  // Special case: Phone input uses inline styles, not Tailwind classes
  if (
    childType === 'ReactPhoneInput' ||
    child.props?.containerClass?.includes('phone-input-container')
  ) {
    return React.cloneElement(child, {
      inputStyle: {
        ...child.props.inputStyle,
        borderRadius: '6px 6px 0 0', // Top corners only
        ...(error && { borderColor: '#ef4444' }), // Red border if error
      },
      buttonStyle: {
        ...child.props.buttonStyle,
        borderRadius: '6px 0 0 0', // Top-left corner only
        ...(error && { borderColor: '#ef4444' }), // Red border if error
      },
    } as any);
  }

  // For all other inputs: add rounded-b-none class AND error styling if needed
  // The [&_input]:rounded-b-none handles nested inputs (e.g., DatePicker)
  // The [&_button]:rounded-b-none handles button-based inputs (Select, MultiSelect)
  // Error classes apply red borders and focus rings
  return React.cloneElement(child, {
    className: cn(
      child.props.className,
      'rounded-b-none [&_input]:rounded-b-none [&_button]:rounded-b-none',
      error &&
        'border-destructive focus-visible:ring-destructive [&_input]:border-destructive [&_input]:focus-visible:ring-destructive [&_button]:border-destructive',
    ),
  } as any);
};

/**
 * InputWithSuggestion - Generic wrapper for adding AI suggestions to any input component
 *
 * This component wraps ANY input component and adds:
 * - Green suggestion chip with accept and hide buttons
 * - Yellow hint/warning banner
 * - Collapsible UI when suggestion is hidden (shows "AI suggestion available")
 * - Automatic hiding when value matches suggestion
 *
 * @component
 * @example
 * ```tsx
 * <InputWithSuggestion
 *   suggestion="John Doe"
 *   hint="Name extracted from caller ID"
 *   field={field}
 *   value={value}
 *   onAccept={() => onChange("John Doe")}
 * >
 *   <Input value={value} onChange={onChange} />
 * </InputWithSuggestion>
 * ```
 */
export const InputWithSuggestion: React.FC<InputWithSuggestionProps> = ({
  children,
  suggestion,
  hint,
  field,
  value,
  onAccept,
  onReject,
  onExpand,
  options,
  onAcceptOption,
  isTextarea = false,
  isCollapsed = false,
  className = '',
  error,
  evidence,
  sourceCallId,
  onNavigateToCall,
  onNavigateToAudit,
}) => {
  // Different negative margins for different input types
  const negativeMargin = isTextarea ? '-mt-2' : '-mt-px';

  // Check if a non-empty, non-matching suggestion exists
  const hasSuggestion =
    suggestion !== undefined &&
    suggestion !== null &&
    value !== suggestion &&
    (typeof suggestion !== 'string' || suggestion.trim() !== '');

  // Determine if suggestion should be visually expanded (not collapsed for textarea)
  const shouldShowSuggestion = hasSuggestion && !(isTextarea && isCollapsed);

  // Check if we have a hint
  const hasHint = hint !== undefined && hint !== null && hint.trim() !== '';

  // Multi-value chooser: shown until the field carries one of the offered
  // values (picking or typing an offered value both settle the choice).
  const hasOptions = !!options && options.length > 0 && !options.some(o => o.value === value);

  // Check if we have both suggestion and hint to render them as connected
  const hasBoth = shouldShowSuggestion && hint;

  // If no suggestion, hint, or options, just render the input
  if (!hasSuggestion && !hasHint && !hasOptions) {
    return <div className={className}>{children}</div>;
  }

  // If collapsed (for textarea only), show collapsed UI
  if (isCollapsed && isTextarea) {
    // Determine collapsed banner style and text based on what's collapsed
    const isHintOnly = hasHint && !hasSuggestion;
    const isBoth = hasSuggestion && hasHint;

    const collapsedConfig = isHintOnly
      ? {
          borderColor: 'border-ai-hint-border',
          bgColor: 'bg-ai-hint',
          iconColor: 'text-ai-hint-icon',
          textColor: 'text-ai-hint-foreground',
          hoverBg: 'hover:bg-ai-hint-border',
          hoverText: 'hover:text-ai-hint-foreground',
          message: 'Review hint available',
        }
      : {
          borderColor: 'border-ai-suggestion-border',
          bgColor: 'bg-ai-suggestion',
          iconColor: 'text-ai-suggestion-icon',
          textColor: 'text-ai-suggestion-foreground',
          hoverBg: 'hover:bg-ai-suggestion-border',
          hoverText: 'hover:text-ai-suggestion-foreground',
          message: isBoth ? 'Review suggestion and hint available' : 'Review suggestion available',
        };

    return (
      <div className={className}>
        {/* Remove bottom radius from input for seamless connection with collapsed banner */}
        {removeBottomRadius(children, error)}
        <div className={negativeMargin}>
          {/* Collapsed AI recommendation - color and message match what's hidden */}
          <div
            className={`flex items-start gap-2 rounded-t-none rounded-b-md border ${collapsedConfig.borderColor} ${collapsedConfig.bgColor} px-3 py-1.5`}
          >
            <Sparkles
              className={`h-4 w-4 ${collapsedConfig.iconColor} shrink-0 mt-0.5`}
              aria-hidden="true"
            />
            <span className={`text-sm ${collapsedConfig.textColor} flex-1 text-left`}>
              {collapsedConfig.message}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                onExpand?.();
              }}
              className={`h-6 w-6 p-0 ${collapsedConfig.textColor} ${collapsedConfig.hoverBg} ${collapsedConfig.hoverText}`}
              title="Show AI recommendation"
              aria-label="Show AI recommendation"
            >
              <Eye className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Otherwise show full suggestion
  return (
    <div className={className}>
      {/* The actual input component - remove bottom radius for seamless connection */}
      {removeBottomRadius(children, error)}

      {/* If both suggestion and hint exist, render them connected */}
      {hasBoth ? (
        <div className={negativeMargin}>
          <SuggestionChip
            suggestion={suggestion}
            field={field}
            onAccept={onAccept}
            onReject={() => onReject?.()}
            showHideButton={isTextarea}
            evidence={evidence}
            sourceCallId={sourceCallId}
            onNavigateToCall={onNavigateToCall}
            onNavigateToAudit={onNavigateToAudit}
            className="rounded-t-none rounded-b-none border-b-0"
          />
          <HintBanner hint={hint} className="-mt-px rounded-t-none rounded-b-md" />
        </div>
      ) : (
        <>
          {/* Suggestion chip only - connected to input */}
          {shouldShowSuggestion && (
            <SuggestionChip
              suggestion={suggestion}
              field={field}
              onAccept={onAccept}
              onReject={() => onReject?.()}
              showHideButton={isTextarea}
              evidence={evidence}
              sourceCallId={sourceCallId}
              onNavigateToCall={onNavigateToCall}
              onNavigateToAudit={onNavigateToAudit}
              className={`${negativeMargin} rounded-t-none`}
            />
          )}

          {/* Hint banner only - connected to input, with hide button if no suggestion and is textarea */}
          {hint && (
            <HintBanner
              hint={hint}
              className={`${negativeMargin} rounded-t-none`}
              onHide={!hasSuggestion && isTextarea ? () => onReject?.() : undefined}
            />
          )}

          {/* Multi-value chooser — the agent picks one, never re-types (R6/R8) */}
          {hasOptions && options && (
            <div
              className={`${negativeMargin} rounded-t-none rounded-b-md border border-ai-suggestion-border bg-ai-suggestion px-3 py-2`}
            >
              <div className="flex items-start gap-2">
                <Sparkles
                  className="h-4 w-4 text-ai-suggestion-icon shrink-0 mt-0.5"
                  aria-hidden="true"
                />
                <span className="flex-1 text-sm text-ai-suggestion-foreground">
                  PMS returned {options.length} values — choose one
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    onReject?.();
                  }}
                  className="h-6 w-6 p-0 text-ai-suggestion-foreground hover:bg-ai-suggestion-border"
                  title="Dismiss PMS options"
                  aria-label="Dismiss PMS options"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="mt-1.5 flex flex-col gap-1" role="listbox" aria-label={field.label}>
                {options.map(option => (
                  <button
                    key={option.value}
                    type="button"
                    role="option"
                    aria-selected={false}
                    onClick={() => onAcceptOption?.(option.value)}
                    className="flex items-baseline gap-2 rounded-md border border-transparent bg-background/60 px-2 py-1.5 text-left text-sm hover:border-ai-suggestion-border hover:bg-background"
                  >
                    <span className="text-foreground">{option.value}</span>
                    {option.label && (
                      <span className="text-xs text-muted-foreground">{option.label}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

InputWithSuggestion.displayName = 'InputWithSuggestion';
